import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ModuleItem } from "../../types/optionalSystems";

export interface ModuleConfiguration {
  name: string;
  size: number;
  techLevel: number;
  description?: string;
  systems: {
    optionalSystems: any[];
    computer: any[];
    computerOptions: Record<string, string[]>; // computerId -> options array
    sensors?: any;
    software: {
      installed: any[];
      running: any[];
      expertSkills: Record<string, string>; // packageId -> skill
      translatorLanguages: Record<string, string>; // packageId -> language
      databaseTopics: Record<string, string>; // packageId -> topic
      virtualCrewRoles: Record<string, string[]>; // packageId -> roles array
      virtualGunnerRoles: Record<string, string[]>; // packageId -> roles array
    };
    weapons?: any[];
    weaponMounts?: any[];
    defensiveSystems?: any[];
    ammunitionStorage?: any[];
    drives?: any[];
    powerPlant: any[];
    bridge?: any;
    crew?: any;
    staterooms?: any[];
    stateroomConfig?: any;
    cargo?: any;
    fuel?: any;
  };
  calculations: {
    totalCost: number;
    totalWeight: number;
    totalPower: number;
    remainingTons: number;
    crewRequirements: number;
    totalCrew: number;
    totalPassengers: number;
    monthlyCost: number;
  };
  supplyUnits?: number;
  supplyTons?: number;
}

export interface ModuleValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  fieldErrors?: Record<string, string[]>;
}

export interface ModuleState {
  currentStep: number;
  moduleData: ModuleItem | null;
  moduleConfig: ModuleConfiguration | null;
  validation: ModuleValidation;
  isDirty: boolean;
  lastSaved: string | null;
  version: string;
}

const initialState: ModuleState = {
  currentStep: 0,
  moduleData: null,
  moduleConfig: null,
  validation: {
    isValid: false,
    errors: [],
    warnings: [],
  },
  isDirty: false,
  lastSaved: null,
  version: "1.0.0",
};

const moduleSlice = createSlice({
  name: "module",
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    
    setModuleData: (state, action: PayloadAction<ModuleItem>) => {
      state.moduleData = action.payload;
      state.isDirty = true;
    },
    
    setModuleConfig: (state, action: PayloadAction<ModuleConfiguration>) => {
      state.moduleConfig = action.payload;
      state.isDirty = true;
    },
    
    updateModuleName: (state, action: PayloadAction<string>) => {
      if (state.moduleConfig) {
        state.moduleConfig.name = action.payload;
        state.isDirty = true;
      }
    },
    
    updateModuleSize: (state, action: PayloadAction<number>) => {
      if (state.moduleConfig) {
        state.moduleConfig.size = action.payload;
        state.isDirty = true;
      }
    },
    
    updateModuleTechLevel: (state, action: PayloadAction<number>) => {
      if (state.moduleConfig) {
        state.moduleConfig.techLevel = action.payload;
        state.isDirty = true;
      }
    },
    

    
    addModuleSystem: (state, action: PayloadAction<{ systemType: string; system: any }>) => {
      if (state.moduleConfig) {
        const { systemType, system } = action.payload;
        if (systemType === "optionalSystems") {
          state.moduleConfig.systems.optionalSystems.push(system);
        } else if (systemType === "powerPlant" || systemType === "computer") {
          (state.moduleConfig.systems as any)[systemType].push(system);
        } else if (systemType in state.moduleConfig.systems) {
          (state.moduleConfig.systems as any)[systemType] = system;
        }
        state.isDirty = true;
      }
    },
    
    removeModuleSystem: (state, action: PayloadAction<{ systemType: string; systemId: string }>) => {
      if (state.moduleConfig) {
        const { systemType, systemId } = action.payload;
        if (systemType === "optionalSystems") {
          state.moduleConfig.systems.optionalSystems = state.moduleConfig.systems.optionalSystems.filter(
            sys => sys.id !== systemId
          );
        } else if (systemType === "powerPlant") {
          state.moduleConfig.systems.powerPlant = state.moduleConfig.systems.powerPlant.filter(
            sys => sys.id !== systemId
          );
        } else if (systemType === "computer") {
          state.moduleConfig.systems.computer = state.moduleConfig.systems.computer.filter(
            sys => sys.id !== systemId
          );
        }
        state.isDirty = true;
      }
    },
    
    updateModuleCalculations: (state, action: PayloadAction<ModuleConfiguration["calculations"]>) => {
      if (state.moduleConfig) {
        state.moduleConfig.calculations = action.payload;
      }
    },
    
    setModuleValidation: (state, action: PayloadAction<ModuleValidation>) => {
      state.validation = action.payload;
    },
    
    clearModuleData: (state) => {
      state.moduleData = null;
      state.moduleConfig = null;
      state.validation = {
        isValid: false,
        errors: [],
        warnings: [],
      };
      state.isDirty = false;
      state.lastSaved = null;
      state.currentStep = 0;
    },
    
    markAsSaved: (state) => {
      state.isDirty = false;
      state.lastSaved = new Date().toISOString();
    },
    
    markAsDirty: (state) => {
      state.isDirty = true;
    },

    // Software actions
    installModuleSoftware: (state, action: PayloadAction<{ packageId: string; level: number }>) => {
      if (state.moduleConfig) {
        const { packageId, level } = action.payload;
        const instanceId = `${packageId}_${level}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        state.moduleConfig.systems.software.installed.push({ packageId, level, installed: true, instanceId });
        state.isDirty = true;
      }
    },

    uninstallModuleSoftware: (state, action: PayloadAction<{ packageId: string; level: number }>) => {
      if (state.moduleConfig) {
        const { packageId, level } = action.payload;
        const existingIndex = state.moduleConfig.systems.software.installed.findIndex(
          (s) => s.packageId === packageId && s.level === level,
        );
        if (existingIndex >= 0) {
          state.moduleConfig.systems.software.installed[existingIndex].installed = false;
        }
        state.isDirty = true;
      }
    },

    startModuleSoftware: (state, action: PayloadAction<{ packageId: string; level: number }>) => {
      if (state.moduleConfig) {
        const { packageId, level } = action.payload;
        const existingIndex = state.moduleConfig.systems.software.running.findIndex(
          (s) => s.packageId === packageId && s.level === level,
        );
        if (existingIndex >= 0) {
          state.moduleConfig.systems.software.running[existingIndex].running = true;
        } else {
          state.moduleConfig.systems.software.running.push({ packageId, level, running: true });
        }
        state.isDirty = true;
      }
    },

    stopModuleSoftware: (state, action: PayloadAction<{ packageId: string; level: number }>) => {
      if (state.moduleConfig) {
        const { packageId, level } = action.payload;
        const existingIndex = state.moduleConfig.systems.software.running.findIndex(
          (s) => s.packageId === packageId && s.level === level,
        );
        if (existingIndex >= 0) {
          state.moduleConfig.systems.software.running[existingIndex].running = false;
        }
        state.isDirty = true;
      }
    },

    setExpertSkill: (state, action: PayloadAction<{ instanceId: string; skill: string }>) => {
      if (state.moduleConfig) {
        const { instanceId, skill } = action.payload;
        if (!state.moduleConfig.systems.software.expertSkills) {
          state.moduleConfig.systems.software.expertSkills = {};
        }
        state.moduleConfig.systems.software.expertSkills[instanceId] = skill;
        state.isDirty = true;
      }
    },

    setTranslatorLanguage: (state, action: PayloadAction<{ instanceId: string; language: string }>) => {
      if (state.moduleConfig) {
        const { instanceId, language } = action.payload;
        if (!state.moduleConfig.systems.software.translatorLanguages) {
          state.moduleConfig.systems.software.translatorLanguages = {};
        }
        state.moduleConfig.systems.software.translatorLanguages[instanceId] = language;
        state.isDirty = true;
      }
    },

    setComputerOptions: (state, action: PayloadAction<{ computerId: string; options: string[] }>) => {
      if (state.moduleConfig) {
        const { computerId, options } = action.payload;
        if (!state.moduleConfig.systems.computerOptions) {
          state.moduleConfig.systems.computerOptions = {};
        }
        state.moduleConfig.systems.computerOptions[computerId] = options;
        state.isDirty = true;
      }
    },

    setDatabaseTopic: (state, action: PayloadAction<{ instanceId: string; topic: string }>) => {
      if (state.moduleConfig) {
        const { instanceId, topic } = action.payload;
        if (!state.moduleConfig.systems.software.databaseTopics) {
          state.moduleConfig.systems.software.databaseTopics = {};
        }
        state.moduleConfig.systems.software.databaseTopics[instanceId] = topic;
        state.isDirty = true;
      }
    },

    setVirtualCrewRoles: (state, action: PayloadAction<{ instanceId: string; roles: string[] }>) => {
      if (state.moduleConfig) {
        const { instanceId, roles } = action.payload;
        if (!state.moduleConfig.systems.software.virtualCrewRoles) {
          state.moduleConfig.systems.software.virtualCrewRoles = {};
        }
        state.moduleConfig.systems.software.virtualCrewRoles[instanceId] = roles;
        state.isDirty = true;
      }
    },

    setVirtualGunnerRoles: (state, action: PayloadAction<{ instanceId: string; roles: string[] }>) => {
      if (state.moduleConfig) {
        const { instanceId, roles } = action.payload;
        if (!state.moduleConfig.systems.software.virtualGunnerRoles) {
          state.moduleConfig.systems.software.virtualGunnerRoles = {};
        }
        state.moduleConfig.systems.software.virtualGunnerRoles[instanceId] = roles;
        state.isDirty = true;
      }
    },

    setModuleSensors: (state, action: PayloadAction<any>) => {
      if (state.moduleConfig) {
        state.moduleConfig.systems.sensors = action.payload;
        state.isDirty = true;
      }
    },

    // Drive actions
    setModuleDrives: (state, action: PayloadAction<any[]>) => {
      if (state.moduleConfig) {
        state.moduleConfig.systems.drives = action.payload;
        state.isDirty = true;
      }
    },

    addModuleDrive: (state, action: PayloadAction<any>) => {
      if (state.moduleConfig) {
        if (!state.moduleConfig.systems.drives) {
          state.moduleConfig.systems.drives = [];
        }
        state.moduleConfig.systems.drives.push(action.payload);
        state.isDirty = true;
      }
    },

    removeModuleDrive: (state, action: PayloadAction<number>) => {
      if (state.moduleConfig && state.moduleConfig.systems.drives) {
        state.moduleConfig.systems.drives.splice(action.payload, 1);
        state.isDirty = true;
      }
    },

    updateModuleDrive: (state, action: PayloadAction<{ index: number; drive: any }>) => {
      if (state.moduleConfig && state.moduleConfig.systems.drives) {
        const { index, drive } = action.payload;
        state.moduleConfig.systems.drives[index] = drive;
        state.isDirty = true;
      }
    },

    // Fuel actions
    setModuleFuel: (state, action: PayloadAction<any>) => {
      if (state.moduleConfig) {
        state.moduleConfig.systems.fuel = action.payload;
        state.isDirty = true;
      }
    },

    // Weapons actions
    addModuleWeapon: (state, action: PayloadAction<any>) => {
      if (state.moduleConfig) {
        if (!state.moduleConfig.systems.weapons) {
          state.moduleConfig.systems.weapons = [];
        }
        state.moduleConfig.systems.weapons.push(action.payload);
        state.isDirty = true;
      }
    },

    removeModuleWeapon: (state, action: PayloadAction<number>) => {
      if (state.moduleConfig && state.moduleConfig.systems.weapons) {
        state.moduleConfig.systems.weapons.splice(action.payload, 1);
        state.isDirty = true;
      }
    },

    updateModuleWeapon: (state, action: PayloadAction<{ index: number; weapon: any }>) => {
      if (state.moduleConfig && state.moduleConfig.systems.weapons) {
        const { index, weapon } = action.payload;
        state.moduleConfig.systems.weapons[index] = weapon;
        state.isDirty = true;
      }
    },

    addModuleWeaponMount: (state, action: PayloadAction<any>) => {
      if (state.moduleConfig) {
        if (!state.moduleConfig.systems.weaponMounts) {
          state.moduleConfig.systems.weaponMounts = [];
        }
        state.moduleConfig.systems.weaponMounts.push(action.payload);
        state.isDirty = true;
      }
    },

    removeModuleWeaponMount: (state, action: PayloadAction<number>) => {
      if (state.moduleConfig && state.moduleConfig.systems.weaponMounts) {
        state.moduleConfig.systems.weaponMounts.splice(action.payload, 1);
        state.isDirty = true;
      }
    },

    updateModuleWeaponMount: (state, action: PayloadAction<{ index: number; mount: any }>) => {
      if (state.moduleConfig && state.moduleConfig.systems.weaponMounts) {
        const { index, mount } = action.payload;
        state.moduleConfig.systems.weaponMounts[index] = mount;
        state.isDirty = true;
      }
    },

    addModuleDefensiveSystem: (state, action: PayloadAction<any>) => {
      if (state.moduleConfig) {
        if (!state.moduleConfig.systems.defensiveSystems) {
          state.moduleConfig.systems.defensiveSystems = [];
        }
        state.moduleConfig.systems.defensiveSystems.push(action.payload);
        state.isDirty = true;
      }
    },

    removeModuleDefensiveSystem: (state, action: PayloadAction<number>) => {
      if (state.moduleConfig && state.moduleConfig.systems.defensiveSystems) {
        state.moduleConfig.systems.defensiveSystems.splice(action.payload, 1);
        state.isDirty = true;
      }
    },

    updateModuleDefensiveSystem: (state, action: PayloadAction<{ index: number; system: any }>) => {
      if (state.moduleConfig && state.moduleConfig.systems.defensiveSystems) {
        const { index, system } = action.payload;
        state.moduleConfig.systems.defensiveSystems[index] = system;
        state.isDirty = true;
      }
    },

    addModuleAmmunition: (state, action: PayloadAction<any>) => {
      if (state.moduleConfig) {
        if (!state.moduleConfig.systems.ammunitionStorage) {
          state.moduleConfig.systems.ammunitionStorage = [];
        }
        state.moduleConfig.systems.ammunitionStorage.push(action.payload);
        state.isDirty = true;
      }
    },

    removeModuleAmmunition: (state, action: PayloadAction<number>) => {
      if (state.moduleConfig && state.moduleConfig.systems.ammunitionStorage) {
        state.moduleConfig.systems.ammunitionStorage.splice(action.payload, 1);
        state.isDirty = true;
      }
    },

    updateModuleAmmunition: (state, action: PayloadAction<{ index: number; ammunition: any }>) => {
      if (state.moduleConfig && state.moduleConfig.systems.ammunitionStorage) {
        const { index, ammunition } = action.payload;
        state.moduleConfig.systems.ammunitionStorage[index] = ammunition;
        state.isDirty = true;
      }
    },

    setModuleSupplyUnits: (state, action: PayloadAction<number>) => {
      if (state.moduleConfig) {
        state.moduleConfig.supplyUnits = action.payload;
        state.moduleConfig.supplyTons = action.payload / 100; // 100 SU = 1 ton
        state.isDirty = true;
      }
    },

    setModuleSupplyTons: (state, action: PayloadAction<number>) => {
      if (state.moduleConfig) {
        state.moduleConfig.supplyTons = action.payload;
        state.moduleConfig.supplyUnits = action.payload * 100; // 1 ton = 100 SU
        state.isDirty = true;
      }
    },

    loadModuleData: (state, action: PayloadAction<ModuleState>) => {
      // Merge the loaded data with current state, preserving some current settings
      const currentValidation = state.validation;
      
      // Replace the entire state with loaded data
      Object.assign(state, action.payload);
      
      // Preserve current validation state
      state.validation = currentValidation;
    },

    resetModule: (state) => {
      state.currentStep = 0;
      state.moduleData = null;
      state.moduleConfig = null;
      state.validation = {
        isValid: false,
        errors: [],
        warnings: [],
      };
      state.isDirty = false;
      state.lastSaved = null;
    },

    // Load module state
    loadModule: (state, action: PayloadAction<ModuleState>) => {
      Object.assign(state, action.payload);
    },
  },
});

export const {
  setCurrentStep,
  setModuleData,
  setModuleConfig,
  updateModuleName,
  updateModuleSize,
  updateModuleTechLevel,
  addModuleSystem,
  removeModuleSystem,
  updateModuleCalculations,
  setModuleValidation,
  clearModuleData,
  markAsSaved,
  markAsDirty,
  installModuleSoftware,
  uninstallModuleSoftware,
  startModuleSoftware,
  stopModuleSoftware,
  setExpertSkill,
  setTranslatorLanguage,
  setComputerOptions,
  setDatabaseTopic,
  setVirtualCrewRoles,
  setVirtualGunnerRoles,
  setModuleSensors,
  setModuleDrives,
  addModuleDrive,
  removeModuleDrive,
  updateModuleDrive,
  setModuleFuel,
  addModuleWeapon,
  removeModuleWeapon,
  updateModuleWeapon,
  addModuleWeaponMount,
  removeModuleWeaponMount,
  updateModuleWeaponMount,
  addModuleDefensiveSystem,
  removeModuleDefensiveSystem,
  updateModuleDefensiveSystem,
  addModuleAmmunition,
  removeModuleAmmunition,
  updateModuleAmmunition,
  setModuleSupplyUnits,
  setModuleSupplyTons,
  loadModuleData,
  resetModule,
  loadModule,
} = moduleSlice.actions;

export default moduleSlice.reducer;
